import { ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import TopBar from '../TopBar';

export interface WorkspaceNavItem {
  label: string;
  path?: string;
  icon: LucideIcon;
  active?: boolean;
  badge?: number;
  items?: WorkspaceNavItem[];
  onClick?: () => void;
  defaultOpen?: boolean;
}

interface WorkspaceShellProps {
  title: string;
  subtitle?: string;
  sectionLabel?: string;
  navItems?: WorkspaceNavItem[];
  utilityItems?: WorkspaceNavItem[];
  actions?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
  headerClassName?: string;
  hideHeaderText?: boolean;
  topBar?: boolean;
}

function renderBadge(count?: number) {
  if (!count) return null;
  return (
    <span className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 text-[11px] font-semibold text-primary-foreground">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function NavEntry({ item, nested = false }: { item: WorkspaceNavItem; nested?: boolean }) {
  const Icon = item.icon;
  const active = Boolean(item.active);
  const baseClassName = [
    'group flex w-full items-center gap-3 rounded-lg text-sm font-medium transition-colors',
    nested ? 'px-3 py-2 pl-10' : 'px-3 py-2.5',
    active
      ? 'bg-primary/10 text-primary'
      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
  ].join(' ');
  const content = (
    <>
      {!nested && <Icon className="h-4 w-4 shrink-0" />}
      <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
      {renderBadge(item.badge)}
      {active && !item.badge && <ChevronRight className="h-4 w-4 shrink-0" />}
    </>
  );

  if (item.path) {
    return <Link to={item.path} className={baseClassName}>{content}</Link>;
  }

  return (
    <button type="button" onClick={item.onClick} className={baseClassName}>
      {content}
    </button>
  );
}

function NavGroup({ item }: { item: WorkspaceNavItem }) {
  const [open, setOpen] = useState(Boolean(item.active) || Boolean(item.defaultOpen));
  const Icon = item.icon;

  useEffect(() => {
    if (item.active) setOpen(true);
  }, [item.active]);

  if (!item.items?.length) return <NavEntry item={item} />;

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen(value => !value)}
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${item.active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
        {renderBadge(item.badge)}
        {open ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
      </button>
      {open && (
        <div className="space-y-1">
          {item.items.map(child => <NavEntry key={`${item.label}-${child.label}`} item={child} nested />)}
        </div>
      )}
    </div>
  );
}

function MobileNav({ items, compact = false }: { items: WorkspaceNavItem[]; compact?: boolean }) {
  const flatItems = items.flatMap(item => item.items?.length ? item.items : [item]).filter(item => item.path || item.onClick);
  if (flatItems.length === 0) return null;

  return (
    <div className={`${compact ? '-mx-4 overflow-x-auto px-4 lg:hidden' : '-mx-4 mt-4 overflow-x-auto border-t border-border px-4 pt-3 lg:hidden'}`}>
      <nav className="flex gap-2 pb-1">
        {flatItems.map(item => {
          const Icon = item.icon;
          const className = `inline-flex h-9 shrink-0 items-center gap-2 rounded-full border px-3 text-sm font-medium transition-colors ${item.active ? 'border-primary/20 bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`;
          const content = (
            <>
              <Icon className="h-4 w-4" />
              <span>{item.label}</span>
              {renderBadge(item.badge)}
            </>
          );
          return item.path ? (
            <Link key={item.path} to={item.path} className={className}>{content}</Link>
          ) : (
            <button key={item.label} type="button" onClick={item.onClick} className={className}>{content}</button>
          );
        })}
      </nav>
    </div>
  );
}

export default function WorkspaceShell({
  title,
  subtitle,
  sectionLabel,
  navItems = [],
  utilityItems = [],
  actions,
  children,
  contentClassName = '',
  headerClassName = '',
  hideHeaderText = false,
  topBar = true,
}: WorkspaceShellProps) {
  const showHeaderText = !hideHeaderText && Boolean(title || subtitle || sectionLabel);
  const showHeaderContent = showHeaderText || Boolean(actions);
  const showMobileNavigation = navItems.length > 0;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background text-foreground">
      {topBar && <TopBar />}
      <div className="flex min-h-0 flex-1">
        {navItems.length > 0 && (
          <aside className="hidden w-72 shrink-0 border-r border-border bg-sidebar lg:flex lg:flex-col">
            <nav className="sidebar-scroll flex-1 space-y-1 overflow-y-auto px-3 py-5">
              {navItems.map(item => <NavGroup key={item.label} item={item} />)}
            </nav>
            {utilityItems.length > 0 && (
              <div className="border-t border-border px-3 py-3">
                <div className="space-y-1">
                  {utilityItems.map(item => <NavEntry key={item.label} item={item} />)}
                </div>
              </div>
            )}
          </aside>
        )}

        <main className="flex min-w-0 flex-1 flex-col overflow-y-auto">
          {(showHeaderContent || showMobileNavigation) && (
            <header className={`border-b border-border bg-background px-4 sm:px-6 ${showHeaderContent ? 'py-4' : 'py-3 lg:hidden'} ${headerClassName}`}>
              {showHeaderContent && (
                <div className={`flex flex-col gap-4 ${showHeaderText ? 'xl:flex-row xl:items-center xl:justify-between' : 'sm:items-end'}`}>
                  {showHeaderText && (
                    <div className="min-w-0">
                      {sectionLabel && <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary lg:hidden">{sectionLabel}</p>}
                      <h2 className="text-2xl font-semibold tracking-normal text-foreground">{title}</h2>
                      {subtitle && <p className="mt-1 max-w-3xl text-sm leading-6 text-muted-foreground">{subtitle}</p>}
                    </div>
                  )}
                  {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
                </div>
              )}
              {showMobileNavigation && <MobileNav items={[...navItems, ...utilityItems]} compact={!showHeaderContent} />}
            </header>
          )}

          <div className={`min-h-0 flex-1 ${contentClassName || 'px-4 py-5 sm:px-6'}`}>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
